/**
 * Compatibility Rules v1 : domain vocabulary and resolver.
 *
 * Does not rank recommendations, compute runtime, or emit customer-facing copy.
 * Unknown stays unknown: no matching VERIFIED rule never defaults to COMPATIBLE.
 */

const RELATIONSHIP_TYPES = Object.freeze([
  'COMPATIBLE_WITH',
  'INCOMPATIBLE_WITH',
  'REQUIRES',
  'REQUIRES_ADAPTER',
  'ALTERNATIVE_TO',
  'REPLACES',
  'SUPERSEDES',
]);

const VERIFICATION_STATUSES = Object.freeze(['VERIFIED', 'CONDITIONAL', 'UNVERIFIED']);

const RESOLVER_STATUSES = Object.freeze([
  'COMPATIBLE',
  'COMPATIBLE_WITH_REQUIREMENT',
  'INCOMPATIBLE',
  'REDUNDANT_OR_NOT_REQUIRED',
  'NOT_FULLY_VERIFIED',
]);

const SOURCE_TYPES = Object.freeze([
  'GARMIN_PRODUCT_PAGE',
  'GARMIN_INSTALLATION_MANUAL',
  'GARMIN_TRANSDUCER_GUIDE',
  'GARMIN_SUPPORT',
  'MANUFACTURER_DOCUMENTATION',
]);

/**
 * @param {object} catalog
 * @returns {Map<string, object>}
 */
function indexBySlug(rows, key = 'slug') {
  const map = new Map();
  for (const row of rows || []) {
    if (row && row[key]) map.set(row[key], row);
  }
  return map;
}

/**
 * Recursively expand selected slugs through package_contents.
 * Nested packages are expanded. Cycles are recorded and skipped (no infinite recursion).
 *
 * @param {string[]} selectedSlugs
 * @param {object} catalog
 * @returns {{ selectedSlugs: string[], expandedSlugs: string[], products: object[], cycles: string[][] }}
 */
function expandPackageContents(selectedSlugs, catalog) {
  const productBySlug = indexBySlug(catalog.products);
  const childrenByPackage = new Map();
  for (const row of Array.isArray(catalog.packageContents) ? catalog.packageContents : []) {
    if (!row?.packageSlug || !row.componentSlug) continue;
    if (!childrenByPackage.has(row.packageSlug)) childrenByPackage.set(row.packageSlug, []);
    childrenByPackage.get(row.packageSlug).push(row.componentSlug);
  }

  const expanded = new Set();
  const cycles = [];

  function walk(slug, path) {
    if (!slug) return;
    if (path.has(slug)) {
      cycles.push([...path, slug]);
      return;
    }
    if (expanded.has(slug)) return;
    expanded.add(slug);
    const product = productBySlug.get(slug);
    const children = childrenByPackage.get(slug) || [];
    if (!product || product.entityKind !== 'package' || children.length === 0) return;
    const nextPath = new Set(path);
    nextPath.add(slug);
    for (const child of children) {
      walk(child, nextPath);
    }
  }

  for (const root of selectedSlugs || []) {
    walk(root, new Set());
  }

  return {
    selectedSlugs: [...(selectedSlugs || [])],
    expandedSlugs: [...expanded],
    products: [...expanded].map((slug) => productBySlug.get(slug)).filter(Boolean),
    cycles,
  };
}

/**
 * @param {string} requiredSlug
 * @param {Set<string>|string[]} expandedSlugs
 * @param {object} [catalog]
 * @returns {{ satisfied: boolean, satisfiedBy: string|null }}
 */
function isComponentSatisfied(requiredSlug, expandedSlugs, catalog) {
  const present = expandedSlugs instanceof Set ? expandedSlugs : new Set(expandedSlugs || []);
  if (present.has(requiredSlug)) {
    return { satisfied: true, satisfiedBy: requiredSlug };
  }
  const productBySlug = indexBySlug(catalog?.products);
  const required = productBySlug.get(requiredSlug);
  if (required?.hardwareFamilySlug) {
    for (const slug of present) {
      const product = productBySlug.get(slug);
      if (product?.hardwareFamilySlug === required.hardwareFamilySlug) {
        return { satisfied: true, satisfiedBy: slug };
      }
    }
  }
  return { satisfied: false, satisfiedBy: null };
}

function refSide(rule, prefix) {
  return {
    productSlug: rule[`${prefix}ProductSlug`] || null,
    hardwareFamilySlug: rule[`${prefix}HardwareFamilySlug`] || null,
  };
}

function entityMatchesSide(product, side) {
  if (!product || !side) return false;
  if (side.productSlug && side.productSlug === product.slug) return true;
  if (side.hardwareFamilySlug && side.hardwareFamilySlug === product.hardwareFamilySlug) {
    return true;
  }
  return false;
}

function isVerified(rule) {
  return rule?.verificationStatus === 'VERIFIED';
}

function toEntityRef(product) {
  if (!product) return null;
  return {
    slug: product.slug,
    name: product.name,
    hardwareFamilySlug: product.hardwareFamilySlug || null,
    entityKind: product.entityKind,
    roles: product.roles || [],
  };
}

function evidenceFromRule(rule) {
  return {
    ruleKey: rule.ruleKey,
    relationshipType: rule.relationshipType,
    verificationStatus: rule.verificationStatus,
    sourceUrl: rule.sourceUrl || null,
    sourceType: rule.sourceType || null,
    verifiedAt: rule.verifiedAt || null,
    notes: rule.notes || null,
  };
}

function matchingRules(subject, object, rules, relationshipType) {
  return (rules || []).filter((rule) => {
    if (relationshipType && rule.relationshipType !== relationshipType) return false;
    return entityMatchesSide(subject, refSide(rule, 'subject'))
      && entityMatchesSide(object, refSide(rule, 'object'));
  });
}

function requiresRulesFor(subject, rules) {
  return (rules || []).filter(
    (rule) =>
      (rule.relationshipType === 'REQUIRES' || rule.relationshipType === 'REQUIRES_ADAPTER')
      && entityMatchesSide(subject, refSide(rule, 'subject'))
  );
}

function requirementObjectSlug(rule) {
  return rule.objectProductSlug || null;
}

function requirementFamilySlug(rule) {
  return rule.objectHardwareFamilySlug || null;
}

function resolveRequirement(rule, expandedSet, catalog) {
  const productSlug = requirementObjectSlug(rule);
  const familySlug = requirementFamilySlug(rule);
  let checkSlug = productSlug;
  if (!checkSlug && familySlug) {
    const match = (catalog.products || []).find(
      (p) => p.hardwareFamilySlug === familySlug && expandedSet.has(p.slug)
    );
    if (match) {
      return {
        relationshipType: rule.relationshipType,
        productSlug: match.slug,
        hardwareFamilySlug: familySlug,
        satisfied: true,
        satisfiedBy: match.slug,
        evidence: evidenceFromRule(rule),
      };
    }
    const canonical = (catalog.products || []).find((p) => p.hardwareFamilySlug === familySlug);
    checkSlug = canonical?.slug || null;
  }

  const satisfaction = checkSlug
    ? isComponentSatisfied(checkSlug, expandedSet, catalog)
    : { satisfied: false, satisfiedBy: null };

  return {
    relationshipType: rule.relationshipType,
    productSlug: checkSlug,
    hardwareFamilySlug: familySlug,
    satisfied: satisfaction.satisfied,
    satisfiedBy: satisfaction.satisfiedBy,
    evidence: evidenceFromRule(rule),
  };
}

function architectureCompatibleRules(sonar, display, rules) {
  const direct = matchingRules(sonar, display, rules, 'COMPATIBLE_WITH').filter(isVerified);
  if (direct.length) return direct;

  const requires = requiresRulesFor(sonar, rules).filter(
    (rule) => rule.relationshipType === 'REQUIRES' && isVerified(rule)
  );
  const viaProcessor = [];
  for (const req of requires) {
    const processor = {
      slug: req.objectProductSlug,
      hardwareFamilySlug: req.objectHardwareFamilySlug,
    };
    const hits = (rules || []).filter((rule) => {
      if (rule.relationshipType !== 'COMPATIBLE_WITH' || !isVerified(rule)) return false;
      if (!entityMatchesSide(display, refSide(rule, 'object'))) return false;
      const subject = refSide(rule, 'subject');
      if (processor.slug && subject.productSlug === processor.slug) return true;
      if (processor.hardwareFamilySlug && subject.hardwareFamilySlug === processor.hardwareFamilySlug) {
        return true;
      }
      return false;
    });
    viaProcessor.push(...hits);
  }
  return viaProcessor;
}

function pairNotes(status, sonar, missing, extraProcessor) {
  const notes = [];
  if (status === 'NOT_FULLY_VERIFIED') {
    notes.push('No matching VERIFIED compatibility rule. Unknown does not default to compatible.');
  }
  if (missing.length) {
    notes.push(
      `Missing required component(s): ${missing.map((m) => m.productSlug || m.hardwareFamilySlug).join(', ')}`
    );
  }
  if (extraProcessor) {
    notes.push(
      `${extraProcessor.slug} is present but is not required by ${sonar.slug}. It is not incompatible.`
    );
  }
  return notes;
}

/**
 * Evaluate one live-sonar × display pair plus requirement satisfaction.
 *
 * @param {object} sonar
 * @param {object} display
 * @param {Set<string>} expandedSet
 * @param {object} catalog
 * @param {object[]} rules
 * @param {object[]} [processorProducts]
 */
function resolvePair(sonar, display, expandedSet, catalog, rules, processorProducts = []) {
  const incompatible = matchingRules(sonar, display, rules, 'INCOMPATIBLE_WITH').filter(isVerified);
  const requirementRows = requiresRulesFor(sonar, rules)
    .filter(isVerified)
    .map((rule) => resolveRequirement(rule, expandedSet, catalog));
  const missing = requirementRows.filter((row) => !row.satisfied);
  const compatibleHits = architectureCompatibleRules(sonar, display, rules);
  const evidence = [
    ...incompatible.map(evidenceFromRule),
    ...compatibleHits.map(evidenceFromRule),
    ...requirementRows.map((row) => row.evidence).filter(Boolean),
  ];

  const extraProcessor = processorProducts.find((processor) => {
    const required = requirementRows.some(
      (row) => row.productSlug === processor.slug || row.satisfiedBy === processor.slug
    );
    return !required;
  }) || null;

  if (incompatible.length) {
    return {
      status: 'INCOMPATIBLE',
      subject: toEntityRef(sonar),
      object: toEntityRef(display),
      requirements: requirementRows,
      evidence,
      verificationStatus: 'VERIFIED',
      notes: pairNotes('INCOMPATIBLE', sonar, missing, extraProcessor),
    };
  }

  if (!compatibleHits.length) {
    return {
      status: 'NOT_FULLY_VERIFIED',
      subject: toEntityRef(sonar),
      object: toEntityRef(display),
      requirements: requirementRows,
      evidence,
      verificationStatus: null,
      notes: pairNotes('NOT_FULLY_VERIFIED', sonar, missing, extraProcessor),
    };
  }

  if (missing.length) {
    return {
      status: 'COMPATIBLE_WITH_REQUIREMENT',
      subject: toEntityRef(sonar),
      object: toEntityRef(display),
      requirements: requirementRows,
      evidence,
      verificationStatus: 'VERIFIED',
      notes: pairNotes('COMPATIBLE_WITH_REQUIREMENT', sonar, missing, extraProcessor),
    };
  }

  return {
    status: 'COMPATIBLE',
    subject: toEntityRef(sonar),
    object: toEntityRef(display),
    requirements: requirementRows,
    evidence,
    verificationStatus: 'VERIFIED',
    notes: pairNotes('COMPATIBLE', sonar, missing, extraProcessor),
  };
}

function assessComponents(expandedProducts, pairResults, catalog) {
  const liveSonar = expandedProducts.filter((p) => (p.roles || []).includes('LIVE_SONAR'));
  const processors = expandedProducts.filter((p) =>
    (p.roles || []).includes('SONAR_PROCESSOR_INTERFACE') && !(p.roles || []).includes('LIVE_SONAR')
  );

  return processors.map((processor) => {
    const requiredBy = [];
    for (const sonar of liveSonar) {
      for (const pair of pairResults) {
        if (pair.subject?.slug !== sonar.slug) continue;
        for (const req of pair.requirements || []) {
          if (req.productSlug === processor.slug || req.satisfiedBy === processor.slug) {
            requiredBy.push(sonar.slug);
          }
        }
      }
    }
    if (requiredBy.length) {
      return {
        slug: processor.slug,
        status: 'REQUIRED',
        requiredBy,
        notes: [`Required by ${requiredBy.join(', ')}`],
      };
    }
    return {
      slug: processor.slug,
      status: 'REDUNDANT_OR_NOT_REQUIRED',
      requiredBy: [],
      notes: [
        `${processor.slug} is not required by the selected LiveScope 2 / integrated-processor sonar. It is not incompatible. A later build layer may treat this as NOT_USED.`,
      ],
    };
  });
}

/**
 * Domain resolver. Accepts canonical product slugs (and packages) plus catalog + rules.
 *
 * @param {{ selectedSlugs: string[], catalog: object, rules: object[] }} input
 */
function evaluateBuildCompatibility(input) {
  const catalog = input.catalog;
  const rules = input.rules || [];
  const expansion = expandPackageContents(input.selectedSlugs || [], catalog);
  const expandedSet = new Set(expansion.expandedSlugs);
  const liveSonar = expansion.products.filter((p) => (p.roles || []).includes('LIVE_SONAR'));
  const displays = expansion.products.filter((p) => (p.roles || []).includes('DISPLAY'));
  const processors = expansion.products.filter(
    (p) => (p.roles || []).includes('SONAR_PROCESSOR_INTERFACE') && !(p.roles || []).includes('LIVE_SONAR')
  );

  const pairResults = [];
  for (const sonar of liveSonar) {
    if (!displays.length) {
      const requirementRows = requiresRulesFor(sonar, rules)
        .filter(isVerified)
        .map((rule) => resolveRequirement(rule, expandedSet, catalog));
      pairResults.push({
        status: requirementRows.some((row) => !row.satisfied)
          ? 'COMPATIBLE_WITH_REQUIREMENT'
          : 'NOT_FULLY_VERIFIED',
        subject: toEntityRef(sonar),
        object: null,
        requirements: requirementRows,
        evidence: requirementRows.map((row) => row.evidence).filter(Boolean),
        verificationStatus: requirementRows.length ? 'VERIFIED' : null,
        notes: ['No display was selected; display relationship remains unresolved.'],
      });
      continue;
    }
    for (const display of displays) {
      pairResults.push(
        resolvePair(sonar, display, expandedSet, catalog, rules, processors)
      );
    }
  }

  if (!liveSonar.length && displays.length) {
    pairResults.push({
      status: 'NOT_FULLY_VERIFIED',
      subject: null,
      object: toEntityRef(displays[0]),
      requirements: [],
      evidence: [],
      verificationStatus: null,
      notes: ['No live sonar was selected; relationship remains unresolved.'],
    });
  }

  return {
    selection: {
      selectedSlugs: expansion.selectedSlugs,
      expandedSlugs: expansion.expandedSlugs,
    },
    pairResults,
    componentAssessments: assessComponents(expansion.products, pairResults, catalog),
    statuses: RESOLVER_STATUSES,
  };
}

function validateCompatibilityRule(rule, catalog) {
  const errors = [];
  if (!rule?.ruleKey) errors.push('rule missing ruleKey');
  if (!RELATIONSHIP_TYPES.includes(rule?.relationshipType)) {
    errors.push(`${rule?.ruleKey}: invalid relationshipType ${rule?.relationshipType}`);
  }
  if (!VERIFICATION_STATUSES.includes(rule?.verificationStatus)) {
    errors.push(`${rule?.ruleKey}: invalid verificationStatus ${rule?.verificationStatus}`);
  }
  const subjectCount = Number(!!rule.subjectProductSlug) + Number(!!rule.subjectHardwareFamilySlug);
  const objectCount = Number(!!rule.objectProductSlug) + Number(!!rule.objectHardwareFamilySlug);
  if (subjectCount !== 1) errors.push(`${rule?.ruleKey}: subject must be a product XOR hardware family`);
  if (objectCount !== 1) errors.push(`${rule?.ruleKey}: object must be a product XOR hardware family`);

  const productBySlug = indexBySlug(catalog?.products);
  const familyBySlug = indexBySlug(catalog?.hardwareFamilies);
  if (rule.subjectProductSlug && !productBySlug.has(rule.subjectProductSlug)) {
    errors.push(`${rule.ruleKey}: unknown subject product ${rule.subjectProductSlug}`);
  }
  if (rule.objectProductSlug && !productBySlug.has(rule.objectProductSlug)) {
    errors.push(`${rule.ruleKey}: unknown object product ${rule.objectProductSlug}`);
  }
  if (rule.subjectHardwareFamilySlug && !familyBySlug.has(rule.subjectHardwareFamilySlug)) {
    errors.push(`${rule.ruleKey}: unknown subject family ${rule.subjectHardwareFamilySlug}`);
  }
  if (rule.objectHardwareFamilySlug && !familyBySlug.has(rule.objectHardwareFamilySlug)) {
    errors.push(`${rule.ruleKey}: unknown object family ${rule.objectHardwareFamilySlug}`);
  }

  const batterySlugs = new Set([
    '8ah-lifepo4-battery-only',
    '19ah-nmc-+-charger',
    '32ah-nmc-+-charger-+-bt',
    '52ah-nmc-+-charger-+-bt',
  ]);
  if (batterySlugs.has(rule.subjectProductSlug) || batterySlugs.has(rule.objectProductSlug)) {
    errors.push(`${rule.ruleKey}: batteries must not appear in the sonar compatibility graph`);
  }

  if (rule.verificationStatus === 'VERIFIED' && !rule.sourceUrl) {
    errors.push(`${rule.ruleKey}: VERIFIED rules require sourceUrl`);
  }
  if (rule.sourceType && !SOURCE_TYPES.includes(rule.sourceType)) {
    errors.push(`${rule.ruleKey}: invalid sourceType ${rule.sourceType}`);
  }
  return errors;
}

function validateCompatibilityRules(rules, catalog) {
  const errors = [];
  const keys = new Set();
  for (const rule of rules || []) {
    if (keys.has(rule.ruleKey)) errors.push(`duplicate ruleKey ${rule.ruleKey}`);
    keys.add(rule.ruleKey);
    errors.push(...validateCompatibilityRule(rule, catalog));
  }
  const liveScope2 = new Set(['livescope-2-lvs44', 'livescope-2-lvs42hd', 'livescope-2-lvs42hd-ice']);
  for (const rule of rules || []) {
    if (rule.relationshipType !== 'REQUIRES') continue;
    if (liveScope2.has(rule.subjectProductSlug)) {
      errors.push(`${rule.ruleKey}: LiveScope 2 must not have a GLS 10 REQUIRES rule`);
    }
    if (
      rule.subjectHardwareFamilySlug === 'garmin-livescope-2'
      || rule.subjectHardwareFamilySlug === 'garmin-livescope-2-hd'
    ) {
      errors.push(`${rule.ruleKey}: LiveScope 2 families must not REQUIRE GLS 10`);
    }
  }
  return errors;
}

module.exports = {
  RELATIONSHIP_TYPES,
  VERIFICATION_STATUSES,
  RESOLVER_STATUSES,
  SOURCE_TYPES,
  expandPackageContents,
  isComponentSatisfied,
  evaluateBuildCompatibility,
  validateCompatibilityRules,
};
